import { CodeBlock, SectionTitle, SubTitle, InfoBox } from './shared';

export default function StreamingContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">流式传输</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 的流式传输机制，包括分块算法、合并策略、类人节奏和 Telegram 草稿流式传输。
        </p>
      </div>

      <SectionTitle id="stream-chunked">分块流式传输（渠道消息）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        流式传输将模型生成的内容分成多个块实时发送，让用户看到实时的生成进度。
        不同渠道对流式传输的支持方式不同：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
        {[
          { channel: 'Telegram', method: '草稿消息实时更新', icon: 'ri-telegram-line' },
          { channel: 'Discord', method: '消息编辑实时更新', icon: 'ri-discord-line' },
          { channel: 'Slack', method: '消息编辑实时更新', icon: 'ri-slack-line' },
          { channel: 'WhatsApp', method: '分块发送多条消息', icon: 'ri-whatsapp-line' },
        ].map((item) => (
          <div key={item.channel} className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-lg px-3 py-2.5">
            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
              <i className={`${item.icon} text-cyan-400`}></i>
            </div>
            <div>
              <div className="text-sm text-white font-medium">{item.channel}</div>
              <div className="text-xs text-slate-400">{item.method}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="stream-algo">分块算法（低/高边界）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        分块算法使用低边界和高边界来控制每个块的大小：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    streaming: {
      chunking: {
        // 低边界：最小块大小（字符数）
        // 低于此值时继续积累，不发送
        lowBoundary: 20,
        // 高边界：最大块大小（字符数）
        // 超过此值时强制发送
        highBoundary: 200,
        // 是否在句子边界处分割
        splitAtSentence: true,
        // 是否在段落边界处分割
        splitAtParagraph: true,
      }
    }
  }
}`} />
      <InfoBox type="info">
        低边界避免发送过于碎片化的内容（如单个字符），高边界避免单条消息过长导致渠道限制。
      </InfoBox>

      <SectionTitle id="stream-merge">合并（合并流式块）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        对于不支持消息编辑的渠道（如 WhatsApp），流式块会被合并后发送：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    streaming: {
      merge: {
        // 是否启用块合并
        enabled: true,
        // 合并窗口时间（毫秒）
        windowMs: 500,
        // 合并后的最大块大小
        maxMergedSize: 500,
      }
    }
  }
}`} />

      <SectionTitle id="stream-rhythm">块之间的类人节奏</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        为了模拟人类打字的自然节奏，OpenClaw 会在块之间添加随机延迟：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    streaming: {
      humanRhythm: {
        // 是否启用类人节奏
        enabled: true,
        // 最小延迟（毫秒）
        minDelayMs: 50,
        // 最大延迟（毫秒）
        maxDelayMs: 200,
        // 是否根据块大小调整延迟
        scaleWithSize: true,
      }
    }
  }
}`} />
      <InfoBox type="tip">
        类人节奏让对话感觉更自然，但会略微增加响应时间。如果追求最快响应，可以禁用此功能。
      </InfoBox>

      <SectionTitle id="stream-or-all">"流式传输块或全部内容"</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        可以配置智能体在不同情况下使用流式传输或一次性发送全部内容：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    streaming: {
      // 流式传输模式：
      // "always" - 始终流式传输
      // "never" - 始终一次性发送
      // "auto" - 根据回复长度自动选择
      mode: "auto",
      // 使用流式传输的最小回复长度（字符）
      // 短于此长度时一次性发送
      minLengthForStreaming: 100,
    }
  }
}`} />

      <SectionTitle id="stream-telegram">Telegram 草稿流式传输（类令牌）</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Telegram 支持一种特殊的流式传输模式：先发送一条"草稿"消息，然后实时编辑更新内容，
        让用户看到类似令牌逐个生成的效果：
      </p>
      <CodeBlock language="json5" code={`{
  channels: [
    {
      type: "telegram",
      botToken: "your_bot_token",
      capabilities: {
        // 启用草稿流式传输
        draftStreaming: true,
        // 草稿更新间隔（毫秒）
        // Telegram API 限制：每条消息每秒最多更新 1 次
        draftUpdateIntervalMs: 1000,
      }
    }
  ]
}`} />
      <InfoBox type="warning">
        Telegram 对消息编辑频率有限制（每条消息每秒最多 1 次）。OpenClaw 会自动遵守此限制，
        但如果更新过于频繁，可能会触发 Telegram 的速率限制。
      </InfoBox>

      <div className="mt-8 bg-slate-800/40 border border-slate-700/50 rounded-lg p-5">
        <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
          <i className="ri-settings-3-line text-cyan-400"></i>
          完整流式传输配置示例
        </h3>
        <CodeBlock language="json5" code={`{
  agent: {
    streaming: {
      enabled: true,
      mode: "auto",
      minLengthForStreaming: 100,
      chunking: {
        lowBoundary: 20,
        highBoundary: 200,
        splitAtSentence: true,
      },
      merge: {
        enabled: true,
        windowMs: 500,
      },
      humanRhythm: {
        enabled: true,
        minDelayMs: 50,
        maxDelayMs: 150,
      }
    }
  }
}`} />
      </div>
    </div>
  );
}
